import { Button } from "@/components/ui/button";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { User } from "@supabase/supabase-js";
import { LogOut, User as UserIcon, Calendar, Plane, Menu, X, BookOpen, Scale } from "lucide-react";

export const Navigation = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Check current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
      <div className="container mx-auto px-4 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-primary-glow flex items-center justify-center">
            <span className="text-white font-bold text-lg">W</span>
          </div>
          <h1 className="text-xl font-bold text-foreground">Wilberforce Pharmacy</h1>
        </Link>
        
        <div className="hidden md:flex items-center gap-4">
          <Link to="/travel-clinic">
            <Button variant="ghost" className="gap-2">
              <Plane className="w-4 h-4" />
              Travel Clinic
            </Button>
          </Link>
          <Link to="/weight-loss-hull">
            <Button variant="ghost" className="gap-2">
              <Scale className="w-4 h-4" />
              Weight Loss
            </Button>
          </Link>
          <Link to="/blog">
            <Button variant="ghost" className="gap-2">
              <BookOpen className="w-4 h-4" />
              Blog
            </Button>
          </Link>
          
          {user ? (
            <>
              <Button 
                variant="ghost" 
                onClick={() => navigate("/dashboard")}
                className="gap-2"
              >
                <Calendar className="w-4 h-4" />
                Dashboard
              </Button>
              <Button 
                variant="outline" 
                onClick={handleSignOut}
                className="gap-2"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="ghost" 
                onClick={() => navigate("/auth")}
              >
                Sign In
              </Button>
              <Button 
                variant="hero" 
                onClick={() => navigate("/auth")}
                className="gap-2"
              >
                <UserIcon className="w-4 h-4" />
                Get Started
              </Button>
            </>
          )}
        </div>

        {/* Mobile Menu Button */}
        <Button 
          variant="ghost" 
          size="sm" 
          className="md:hidden"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background border-t border-border px-4 py-4 space-y-3">
          <Link to="/travel-clinic" onClick={() => setMobileMenuOpen(false)}>
            <Button variant="ghost" className="w-full justify-start gap-2">
              <Plane className="w-4 h-4" />
              Travel Clinic
            </Button>
          </Link>
          <Link to="/weight-loss-hull" onClick={() => setMobileMenuOpen(false)}>
            <Button variant="ghost" className="w-full justify-start gap-2">
              <Scale className="w-4 h-4" />
              Weight Loss
            </Button>
          </Link>
          <Link to="/blog" onClick={() => setMobileMenuOpen(false)}>
            <Button variant="ghost" className="w-full justify-start gap-2">
              <BookOpen className="w-4 h-4" />
              Blog
            </Button>
          </Link>
          
          {user ? (
            <>
              <Button 
                variant="ghost" 
                onClick={() => { navigate("/dashboard"); setMobileMenuOpen(false); }}
                className="w-full justify-start gap-2"
              >
                <Calendar className="w-4 h-4" />
                Dashboard
              </Button>
              <Button 
                variant="outline" 
                onClick={() => { handleSignOut(); setMobileMenuOpen(false); }}
                className="w-full justify-start gap-2"
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="ghost" 
                onClick={() => { navigate("/auth"); setMobileMenuOpen(false); }}
                className="w-full justify-start"
              >
                Sign In
              </Button>
              <Button 
                variant="hero" 
                onClick={() => { navigate("/auth"); setMobileMenuOpen(false); }}
                className="w-full justify-start gap-2"
              >
                <UserIcon className="w-4 h-4" />
                Get Started
              </Button>
            </>
          )}
        </div>
      )}
    </nav>
  );
};